import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,  
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  Rating,
  Tooltip,
  Typography,
} from "@mui/material";
import { Call, Close, Email, SearchOutlined, SmartDisplay } from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { IProducerAssignmentDialogProps } from "../types/crew-dashboard";
import { StreamResponse } from "../CrewDashboard";
import { RoleSkillTool } from "../types/add-crew-member";
import { PilotAPIResponse } from "../types/view-pilot";
import { TrashIcon } from "../../../components/icons";

interface AssignmentResponseProps {
  crew_member: {
    full_name: string;
  };
}

interface CallsheetProps {
  id: number;
  assignment: Array<{
    role: string;
    notes: string | null;
  }>;
  crew_member: PilotAPIResponse;
}

export const ProducerAssignmentDialog: React.FC<IProducerAssignmentDialogProps> = ({
  setShowProducerAssignmentDialog,
  showProducerAssignmentDialog,
  selectedStream
}) => {
  const [producerOptions, setProducerOptions] = useState<PilotAPIResponse[]>([]);
  const [stream, setStream] = useState<StreamResponse | null>(null);
  const [searching, setSearching] = useState<string>('');
  const [searched, setSearched] = useState<PilotAPIResponse[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<CallsheetProps[]>([]);
  const [producersLoading, setProducersLoading] = useState<boolean>(false);
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [callsheetLoading, setCallsheetLoading] = useState<boolean>(false);
  const [producersError, setProducersError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [callsheetError, setCallsheetError] = useState<string | null>(null);

  const [producerRoles, setProducerRoles] = useState<number[]>([]);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const closeAndReset = () => {
    setShowProducerAssignmentDialog(false);
    setProducerOptions([]);
  };

  const fetchProducerRole = async () => {
    try {
      const role = await apiService.get<{ results: RoleSkillTool[] }>(
        `/api/crews/roles/`,
        { search: 'producer' }
      );

      const prods = role.data.results.map(prod => {
        return prod.id
      })

      setProducerRoles(prods);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        "Failed to load producer roles. Please try again.";
      toast.error(errorMessage);
    }
  };

  const fetchCallsheet = async () => {
    setCallsheetLoading(true);
    setCallsheetError(null);
    try {
      const callsheet = await apiService.get<{ results: CallsheetProps[] }>(
        `/api/call-sheets/callsheet/?stream=${selectedStream}`);
      
      setCurrentAssignments(callsheet.data.results);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch callsheet. Try again.";
      setCallsheetError(errorMessage);
      setCurrentAssignments([]);
    } finally {
      setCallsheetLoading(false);
    }
  };
  
  const fetchStream = async () => {
    setStreamLoading(true);
    setStreamError(null);
    try {
      const strm = await apiService.get<StreamResponse>(
        `/api/streams/stream/${selectedStream}/`);
      
      setStream(strm.data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch stream. Try again.";
      setStreamError(errorMessage);
      setStream(null);
    } finally {
      setStreamLoading(false);
    }
  };

  const fetchProducers = async () => {
    setProducersLoading(true);
    setProducersError(null);
    try {
      const producers = await apiService.get<{ results: PilotAPIResponse[] }>(
        `/api/crews/crew-members/`, { page_size: 100 });

      const filtered = producers.data.results.filter(prod => {
        return prod?.roles_detail?.some((role: RoleSkillTool) =>
          role.name.toLowerCase().includes('producer')
        );
      });
      
      setProducerOptions(filtered);
    } catch (err: any) {
      console.error("Failed to fetch producers:", err);
      const errorMessage = 
        err.response?.data?.message ||
        "Failed to load producers. Please try again.";
      setProducersError(errorMessage);
      setProducerOptions([]);
    } finally {
      setProducersLoading(false);
    }
  };

  useEffect(() => {
    if (producerOptions.length !== 0) {
      const filtering = producerOptions.filter(prod => {
        const searchedProducers = !searching ||
          prod.full_name.toLowerCase().includes(searching.toLowerCase());

        return searchedProducers;
      });

      setSearched(filtering);
    }
  }, [searching, producerOptions]);

  const handleProducerAssignment = async (producer: number) => {
    setAssignmentLoading(true);
    try {
      const payload = {
        stream_id: stream?.id,
        crew_member_id: producer,
        assignment: [{
          role: 'PRODUCER',
          notes: ''
        }],
        roles_ids: producerRoles
      };

      const assignment = await apiService.post<AssignmentResponseProps>(
        `/api/call-sheets/callsheet/`, payload);

      toast.success(
        `Successfully assigned ${assignment.data.crew_member.full_name} as Producer for the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (err: any) {
      console.error("Failed to assign producer:", err);
      toast.error(err.response?.data?.message || "Failed to assign producer. Please try again.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleUnAssignment = async (id: number, producer: string) => {
    setAssignmentLoading(true);
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${id}/`);

      toast.success(
        `Successfully unassigned Pixel Pilot: ${producer} as Producer for the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (err: any) {
      console.error("Failed to unassign producer:", err);
      toast.error(err.response?.data?.message || "Failed to unassign producer. Please try again.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const retryStream = () => {
    fetchStream();
  };

  const retryProducers = () => {
    fetchProducers();
  };

  const retryCallsheet = () => {
    fetchCallsheet();
  };

  useEffect(() => {
    if (selectedStream) {
      fetchProducerRole();
      fetchStream();
      fetchCallsheet();
    }
  }, [selectedStream]);

  useEffect(() => {
    fetchProducers();
  }, [showProducerAssignmentDialog]);

  return (
    <Dialog
      open={showProducerAssignmentDialog}
      onClose={closeAndReset}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Assign Producer
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* ASSIGNMENTS */}
        <Box sx={{ my: 1 }}>
          {streamLoading ? (
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Chip
                label={
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    <CircularProgress color={"primary"} size={16} />
                    <i>Fetching Stream.</i>
                  </Box>
                }
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  borderColor: reduxTheme.primary_colors[800],
                }}
              />
            </Box>
          ) : streamError ? (
            <Alert severity="error" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {streamError}
                <Button
                  onClick={() => retryStream()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : !stream ? (
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {'Could not fetch stream.'}
                <Button
                  onClick={() => retryStream()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : (
            <Box
              display={'flex'}
              sx={{
                alignItems: 'center',
                backgroundColor: reduxTheme.primary_colors[800],
                padding: 2,
                borderRadius: 2  
              }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    backgroundColor: reduxTheme.primary_colors[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {assignmentLoading ? (
                    <CircularProgress sx={{ color: '#FFF' }} size={24} />
                  ) : (
                    <SmartDisplay sx={{ color: '#FFF', fontSize: 32 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant={'caption'}>Booking Confirmed</Typography>
                  <Typography variant={'subtitle1'} fontWeight={600}>{stream.title}</Typography>
                  <Typography variant={'caption'}>
                    {`${format(stream.date, 'dd MMM yyyy')} - ${stream.start}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* SEARCH BAR */}
          <Box sx={{ my: 1 }}>
            <Box
              sx={{
                backgroundColor: '',
                border: '1px solid',
                borderRadius: 2,
                borderColor: reduxTheme.primary_colors[300],
                display: "flex",
                alignItems: "center",
                py: .4,
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}>
                <SearchOutlined sx={{ fontSize: 24, mr: 1, color: reduxTheme.primary_colors[300] }} />
                <InputBase
                  placeholder="Search by crew member name..."
                  sx={{
                    padding: .4, flex: 1, color: '#757575'
                  }}
                  onChange={(e) => setSearching(e.target.value)}
                />
            </Box>
          </Box>

          {producersLoading ? (
            <Box display={'flex'} alignItems={'center'} mb={1} gap={1}>
              <Chip
                label={
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    <CircularProgress color={"primary"} size={16} />
                    <i>Fetching Producers.</i>
                  </Box>
                }
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  borderColor: reduxTheme.primary_colors[800],
                }}
              />
            </Box>
          ) : producersError ? (
            <Alert severity="error" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {producersError}
                <Button
                  onClick={() => retryProducers()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : producerOptions?.length === 0 ? (
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {'Could not fetch producers.'}
                <Button
                  onClick={() => retryProducers()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {searched && searched.map(prod => {
                return (
                  <Grid
                    size={3}
                    key={prod.id}
                    sx={{
                        alignItems: 'center',
                        backgroundColor: reduxTheme.primary_colors[800],
                        padding: 2,
                        borderRadius: 2
                    }}>
                    <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={1}>
                        <Avatar src={prod.user.profile_picture ?
                          prod.user.profile_picture : `./placeholder.svg`}
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 50
                          }}
                        />
                        <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
                          <Typography textAlign={'center'} variant={'subtitle2'}>{prod.full_name}</Typography>
                          <Rating size="small" value={prod.average_rating ? prod.average_rating : 0} precision={0.1} readOnly />
                        </Box>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
                      <Chip
                        label={'Available'}
                        size={'small'}
                        sx={{
                          my: 1,
                          borderRadius: 20
                        }}
                      />
                      <Box sx={{ display: "flex", mb: .4, gap: 1 }}>
                        <Tooltip title={prod.user.phone_number || 'No phone number'}>
                          <IconButton
                            disabled={!prod.user.phone_number}
                            onClick={() =>
                              prod.user.phone_number &&
                              window.open(`tel:${prod.user.phone_number}`)
                            }
                            sx={{
                              color: prod.user.phone_number
                                ? reduxTheme.primary_colors[400]
                                : reduxTheme.primary_colors[800],
                              opacity: prod.user.phone_number ? 1 : 0.5,
                              "&:hover": {
                                color: prod.user.phone_number
                                  ? reduxTheme.primary_colors[400]
                                  : reduxTheme.primary_colors[800],
                              },
                            }}
                          >
                            <Call fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={prod.user.email || 'No email address'}>
                          <IconButton
                            disabled={!prod.user.email}
                            onClick={() =>
                              prod.user.email &&
                              window.open(`mailto:${prod.user.email}`)
                            }
                            sx={{
                              color: prod.user.email
                                ? reduxTheme.primary_colors[400]
                                : reduxTheme.primary_colors[800],
                              opacity: prod.user.email ? 1 : 0.5,
                              "&:hover": {
                                color: prod.user.email
                                  ? reduxTheme.primary_colors[400]
                                  : reduxTheme.primary_colors[800],
                              },
                            }}
                          >
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        fullWidth
                        disabled={
                          assignmentLoading || (currentAssignments?.find(ca => ca.crew_member.id === prod.id) ? true : false)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProducerAssignment(prod.id);
                        }}
                        variant={"contained"}>
                        {'Assign'}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* CURRENT ASSIGNMENTS */}
        <Box sx={{ mt: 1, pt: 1, borderWidth: 1, borderTopStyle: 'solid', borderColor: 'divider' }}>
          <Box display={'flex'} alignItems={'center'} mb={1} gap={1}>
            <Typography variant={'subtitle2'}>Current Assignments</Typography>
            {callsheetLoading && (
              <CircularProgress color={"primary"} size={16} />
            )}
          </Box>

          <Box sx={{
            width: '100%',
            backgroundColor: reduxTheme.primary_colors[800],
            mt: 1,
            padding: 1,
            borderRadius: 2
          }}>
            <Grid
              container
              spacing={2}
              sx={{
                mb: 1,
                bgcolor: reduxTheme.primary_colors[800],
                padding: .4,
                borderRadius: 1
              }}>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Role</Typography>
              </Grid>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Pilot Name</Typography>
              </Grid>
              <Grid size={3.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Phone</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Email</Typography>
              </Grid>
              <Grid size={1} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Actions</Typography>
              </Grid>
            </Grid>
            {callsheetError && (
              <Alert severity="error" sx={{ alignItems: 'center' }}>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                  {callsheetError}
                  <Button
                    onClick={() => retryCallsheet()}
                    sx={{
                      color: reduxTheme.primary_colors[400],
                      backgroundColor: reduxTheme.primary_colors[900],
                    }}>
                    Retry
                  </Button>
                </Box>
              </Alert>
            )}
            {currentAssignments?.length ? (
              currentAssignments.map((ca, index) => {
                const rowBg = index % 2 === 0 ? '#FFF' : 'transparent';
                return (
                  <Grid
                    container
                    key={ca.id}
                    display={'flex'}
                    alignItems={'center'}
                    spacing={2}
                    sx={{
                      mb: 1,
                      bgcolor: rowBg,
                      padding: 1,
                      borderRadius: 1
                    }}>
                    <Grid size={3}>
                      <Typography variant={'body2'}>{ca.assignment[0].role}</Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant={'body2'}>{ca.crew_member.full_name}</Typography>
                    </Grid>
                    <Grid size={3.5}>
                      <Typography variant={'body2'}>{ca.crew_member.user.email}</Typography>
                    </Grid>
                    <Grid size={1.5}>
                      <Typography variant={'body2'}>{ca.crew_member.user.phone_number}</Typography>
                    </Grid>
                    <Grid size={1}>
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUnAssignment(ca.id, ca.crew_member.full_name);
                        }}>
                        <TrashIcon color="error" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Alert severity="warning" sx={{ alignItems: 'center' }}>
                {`No Callsheet Has Been Created For The Stream: ${stream?.title} Yet.`}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          disabled={assignmentLoading}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProducerAssignmentDialog;
